/**
 * The night manager's entry point: a top-bar button and an anchored popover.
 *
 * Two views in one surface. The compact one answers "is it working and how far along", which
 * is what someone checking their phone at 2am needs. The expanded one carries the goal builder,
 * the activity list and the live edits, which is what someone sitting down to set a run up
 * needs. Making them one popover rather than a page is deliberate: supervision is something
 * you glance at from wherever you already are.
 *
 * Things the interface must get right, because they are the ones that cost trust:
 *
 * - **Closing it never stops a run.** The popover is a window onto durable state, not the
 *   thing holding it up. Navigating away, closing the browser and logging out all leave the
 *   run exactly as it was.
 * - **Stop is always visible** while a run is enabled, and says what it does: supervision
 *   stops, tickets continue.
 * - **Escape closes, focus is managed, every control is labelled.** This gets used at night on
 *   a laptop, at zoom, and sometimes from a keyboard only.
 * - **Acknowledged saves.** Every edit shows its own saving/saved/failed state, because an
 *   optimistic UI over an asynchronous, version-checked write is how people come to believe a
 *   limit was raised when it was refused.
 */
import { useCallback, useEffect, useId, useRef, useState } from 'react';

import type { NightManagerProjectView, NightManagerRunView, NightManagerEventView } from '@nightshift/ui-types';

import { api } from '../../live/api.ts';
import { useLive } from '../../live/context.tsx';
import { describeEvent, formatAge, formatRemaining, overviewLabel, statusLabel } from '../../lib/night-manager.ts';
import { GoalBuilder } from './goal-builder.tsx';

/** Widest the popover gets. Narrower viewports get the viewport minus a margin. */
const PANEL_MAX_WIDTH = 560;

type SaveState = { kind: 'idle' } | { kind: 'saving' } | { kind: 'saved'; at: number } | { kind: 'failed'; error: string };

const WAIT_REASON_TEXT: Record<string, string> = {
  'waiting-for-ticket-changes': 'Waiting for ticket changes',
  'runner-unavailable': 'Runner unavailable',
  'authentication-required': 'Authentication required on the runner',
  'recovering-session': 'Recovering the session',
  'blocked-ticket-needs-attention': 'A blocked ticket needs attention',
  'no-capable-route': 'No configured route can host a turn',
  'turn-in-flight': 'Working',
};

export function NightManagerButton({ onOpenTicket }: { onOpenTicket: (key: string, run?: number, project?: string | null) => void }) {
  const state = useLive();
  const [open, setOpen] = useState(false);
  const [project, setProject] = useState<NightManagerProjectView | null>(null);
  const [overview, setOverview] = useState<Awaited<ReturnType<typeof api.nightManagerOverview>> | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const labelId = useId();

  const slug = state.projectSlug;

  const load = useCallback(async () => {
    try {
      const summary = await api.nightManagerOverview();
      setOverview(summary);
    } catch {
      // The Live pill already reports a transport outage; do not turn it into a second alert.
    }
    if (!slug) {
      setProject(null);
      return;
    }
    try {
      setProject(await api.nightManagerProject(slug));
    } catch {
      setProject(null);
    }
  }, [slug]);

  useEffect(() => {
    void load();
  }, [load, state.snapshot?.revision]);

  // A run moves without anyone touching the browser, so the popover refreshes while it is
  // open. Closed, it costs nothing: the button's label rides the ordinary snapshot revision.
  useEffect(() => {
    if (!open) return undefined;
    const timer = window.setInterval(() => void load(), 10_000);
    return () => window.clearInterval(timer);
  }, [open, load]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
        buttonRef.current?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  useEffect(() => {
    if (open) panelRef.current?.focus();
  }, [open]);

  /*
   * Anchor the panel to the button but clamp it into the viewport.
   *
   * A plain `right: 0` on a relatively-positioned wrapper is fine on a wide screen and wrong
   * on a narrow one: the panel is wider than the distance from the button to the left edge, so
   * it hangs off the side and its first controls are unreachable. Measuring and clamping is
   * the only version that holds at every width and zoom, which is where this gets used.
   */
  useEffect(() => {
    if (!open) return undefined;
    const place = () => {
      const button = buttonRef.current;
      const panel = panelRef.current;
      if (!button || !panel) return;
      /*
       * Two coordinate spaces, and mixing them is the whole difficulty.
       *
       * The interface scale is `zoom` on the document root, so `getBoundingClientRect` answers
       * in painted pixels while `style.left` is written in layout pixels. Clamping a layout
       * offset against `window.innerWidth` without converting put the panel off the edge at
       * 135%, which is a supported zoom. `--ui-zoom-inverse` is the codebase's existing
       * correction for exactly this, with 1 as the fallback for an engine that lacks it.
       */
      const inverse = Number(getComputedStyle(document.documentElement).getPropertyValue('--ui-zoom-inverse')) || 1;
      const viewport = window.innerWidth * inverse;
      const margin = 8;
      const rightEdge = button.getBoundingClientRect().right * inverse;
      const bottomEdge = button.getBoundingClientRect().bottom * inverse;
      // Width is set here rather than left to CSS so the measurement and the layout cannot
      // disagree: a `min()` in a stylesheet recomputes on its own schedule, and placing
      // against a width that is about to change is the other way this hangs off the edge.
      const width = Math.min(PANEL_MAX_WIDTH, viewport - margin * 2);
      panel.style.width = `${width}px`;
      const maxLeft = Math.max(margin, viewport - width - margin);
      panel.style.left = `${Math.min(Math.max(margin, rightEdge - width), maxLeft)}px`;
      panel.style.top = `${bottomEdge + 6}px`;
    };
    place();
    // The panel's width is not final on the first frame -- its content is still laying out --
    // and placing against a half-measured width leaves it hanging off the right edge once it
    // grows. Following its size is the only version that stays correct as the run's status
    // text, warnings and activity change its height and width.
    const observer = new ResizeObserver(place);
    if (panelRef.current) observer.observe(panelRef.current);
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open]);

  const run = project?.run ?? null;
  const label = slug ? statusLabel(run) : overviewLabel(overview);
  const tone =
    label === 'Needs attention' ? 'var(--amber, #d6a84f)' : label === 'Enabled' ? 'var(--good, #6f9b75)' : undefined;

  return (
    <div style={{ position: 'relative' }}>
      <button
        ref={buttonRef}
        type="button"
        className="btn btn-ghost btn-sm"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={`Night manager: ${label}`}
        title={
          label === 'Disabled'
            ? 'No night manager is supervising this project'
            : `Night manager is ${label.toLowerCase()}`
        }
        style={{ color: tone }}
        onClick={() => setOpen((value) => !value)}
      >
        <span
          aria-hidden
          style={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            background: label === 'Disabled' ? 'var(--dim, #6b7280)' : 'currentColor',
          }}
        />
        Night manager
      </button>
      {open ? (
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: 149 }} onClick={() => setOpen(false)} role="presentation" />
          <div
            ref={panelRef}
            className="menu night-manager-popover"
            role="dialog"
            aria-modal="false"
            aria-labelledby={labelId}
            tabIndex={-1}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              zIndex: 150,
              width: PANEL_MAX_WIDTH,
              maxHeight: 'min(70vh, 720px)',
              overflowY: 'auto',
              padding: 12,
            }}
          >
            <header style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 8 }}>
              <h2 id={labelId} style={{ margin: 0, fontSize: 'var(--fs-body)' }}>
                Night manager
              </h2>
              <span className="dim" style={{ fontSize: 'var(--fs-micro)' }}>
                {label}
              </span>
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                style={{ marginLeft: 'auto' }}
                aria-label="Close night manager"
                onClick={() => {
                  setOpen(false);
                  buttonRef.current?.focus();
                }}
              >
                Close
              </button>
            </header>

            {!slug ? (
              <AllProjectsView overview={overview} />
            ) : project ? (
              <ProjectView key={`${project.projectSlug}:${project.run?.id ?? "none"}`} project={project} onChanged={load} onOpenTicket={onOpenTicket} />
            ) : (
              <p className="dim" style={{ fontSize: 'var(--fs-small)' }}>Loading…</p>
            )}
          </div>
        </>
      ) : null}
    </div>
  );
}

/**
 * The All projects scope.
 *
 * A manager belongs to exactly one project, so this deliberately does not offer an Enable
 * button: it lists what is running, with links, and asks the person to pick a project first.
 * Enabling "the current scope" when the scope is every project is how a run ends up on the
 * wrong one.
 */
function AllProjectsView({ overview }: { overview: Awaited<ReturnType<typeof api.nightManagerOverview>> | null }) {
  const runs = overview?.runs ?? [];
  return (
    <div style={{ fontSize: 'var(--fs-small)' }}>
      {runs.length === 0 ? (
        <p className="dim" style={{ margin: 0 }}>No night manager is supervising any project you can see.</p>
      ) : (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 6 }}>
          {runs.map((entry) => (
            <li key={entry.runId} style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
              <a href={`/p/${encodeURIComponent(entry.projectSlug)}/ops`}>{entry.projectName}</a>
              <span className="dim" style={{ fontSize: 'var(--fs-micro)' }}>
                {entry.needsAttention ? 'needs attention' : entry.status}
                {entry.target !== null ? ` · ${entry.completed}/${entry.target}` : ` · ${entry.completed} completed`}
              </span>
            </li>
          ))}
        </ul>
      )}
      <p className="dim" style={{ marginTop: 10, fontSize: 'var(--fs-micro)' }}>
        A night manager supervises one project. Choose a project in the scope picker to configure one.
      </p>
    </div>
  );
}

function ProjectView({
  project,
  onChanged,
  onOpenTicket,
}: {
  project: NightManagerProjectView;
  onChanged: () => Promise<void> | void;
  onOpenTicket: (key: string, run?: number, project?: string | null) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  // After a stop the popover keeps showing that run -- its reason, its final count and its
  // handoff are the whole point of stopping. `again` is how a person leaves it behind and
  // configures a fresh one, which starts from the saved project defaults rather than from the
  // stopped run's goal.
  const [again, setAgain] = useState(false);
  const run = project.run;

  if (run?.status === 'stopped' && again) {
    return project.canManage ? (
      <GoalBuilder project={project} onEnabled={async () => { setAgain(false); await onChanged(); }} />
    ) : (
      <p className="dim" style={{ fontSize: 'var(--fs-small)', margin: 0 }}>
        Enabling a night manager needs an administrator.
      </p>
    );
  }

  if (!run) {
    return project.canManage ? (
      <GoalBuilder project={project} onEnabled={async () => { setAgain(false); await onChanged(); }} />
    ) : (
      <p className="dim" style={{ fontSize: 'var(--fs-small)', margin: 0 }}>
        No night manager is supervising {project.projectName}. Enabling one needs an administrator.
      </p>
    );
  }

  return (
    <div style={{ fontSize: 'var(--fs-small)', display: 'grid', gap: 10 }}>
      <RunStatus run={run} canManage={project.canManage} onChanged={onChanged} onOpenTicket={onOpenTicket} />
      <div style={{ display: 'flex', gap: 8 }}>
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          aria-expanded={expanded}
          onClick={() => setExpanded((value) => !value)}
        >
          {expanded ? 'Hide activity and settings' : 'Show activity and settings'}
        </button>
        {run.status === 'stopped' && project.canManage ? (
          <button type="button" className="btn btn-sm" onClick={() => setAgain(true)}>
            Run again
          </button>
        ) : null}
      </div>
      {expanded ? (
        <ExpandedView project={project} run={run} onChanged={onChanged} onOpenTicket={onOpenTicket} />
      ) : null}
    </div>
  );
}

function RunStatus({
  run,
  canManage,
  onChanged,
  onOpenTicket,
}: {
  run: NightManagerRunView;
  canManage: boolean;
  onChanged: () => Promise<void> | void;
  onOpenTicket: (key: string, run?: number, project?: string | null) => void;
}) {
  const [stopping, setStopping] = useState<SaveState>({ kind: 'idle' });
  const remaining = run.status === 'enabled' ? formatRemaining(run.msRemaining) : null;

  return (
    <section aria-label="Night manager status" style={{ display: 'grid', gap: 6 }}>
      <p style={{ margin: 0 }}>{run.summary}</p>
      <a href={`/api/night-manager/runs/${encodeURIComponent(run.id)}/report`} download>Export recorded run report</a>

      <dl style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 6, margin: 0 }}>
        <Stat
          label="Completed"
          value={run.progress.target === null ? String(run.progress.completed) : `${run.progress.completed} / ${run.progress.target}`}
        />
        <Stat label="Active" value={`${run.load.active} of ${run.load.limit}`} />
        <Stat label="Blocked" value={String(run.load.blocked)} />
        {run.activity ? <>
          <Stat label="Last activity" value={new Date(run.activity.lastActivityAt).toLocaleTimeString()} />
          <Stat label="Manager process" value={run.activity.activeTool ? `Running ${run.activity.activeTool}` : run.activity.activity} />
        </> : null}
        {run.timing ? <>
          <Stat label="Elapsed" value={`${Math.round(run.timing.elapsedMs / 1000)}s`} />
          <Stat label="In manager turns" value={`${Math.round(run.timing.turnMs / 1000)}s`} />
          <Stat label="Waiting outside turns" value={`${Math.round(run.timing.waitingMs / 1000)}s`} />
        </> : null}
        <Stat label="Known cost (USD)" value={`$${run.usage.knownUsd.toFixed(2)}`} />
        <Stat label="Unpriced invocations" value={`${run.usage.unknownInvocations} of ${run.usage.invocations}`} />
        <Stat label="Reported input / output tokens" value={`${run.usage.tokensIn ?? 'unknown'} / ${run.usage.tokensOut ?? 'unknown'}`} />
        {remaining ? <Stat label="Time remaining" value={remaining} /> : null}
      </dl>

      <p className="dim" style={{ margin: 0, fontSize: 'var(--fs-micro)' }}>
        {run.status === 'stopped'
          ? `Stopped ${run.stoppedAt ? new Date(run.stoppedAt).toLocaleString() : ''} — ${run.stopReason ?? 'unknown reason'}`
          : WAIT_REASON_TEXT[run.waitReason ?? ''] ?? 'Supervising'}
        {run.lastCheckAt ? ` · last check ${new Date(run.lastCheckAt).toLocaleTimeString()}` : ''}
        {run.nextCheckAt && run.status === 'enabled' ? ` · next ${new Date(run.nextCheckAt).toLocaleTimeString()}` : ''}
      </p>

      {run.usage.unknownInvocations > 0 ? <p className="dim" style={{ margin: 0, fontSize: 'var(--fs-micro)' }}>Total dollar cost is unknown. Subscription usage has no assumed marginal price.</p> : null}
      {run.routes.length > 0 ? (
        <p className="dim" style={{ margin: 0, fontSize: 'var(--fs-micro)' }}>
          Route: {run.routes.map((route) => `${route.machine ?? 'any machine'} · ${route.harness} · ${route.model}${route.thinking ? `/${route.thinking}` : ''}`).join(' → ')}
        </p>
      ) : null}

      {run.capacity.length > 0 ? (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0, fontSize: 'var(--fs-micro)' }}>
          {run.capacity.map((reading) => (
            <li key={reading.label} className="dim">
              {reading.label}:{' '}
              {reading.unavailable === null
                ? `${reading.remainingPercent}% left, stops below ${reading.thresholdPercent}%`
                : `no fresh reading (${formatAge(reading.lastKnownAgeMs)}${reading.lastKnownPercent === null ? '' : `, last ${reading.lastKnownPercent}%`}) — supervision continues`}
            </li>
          ))}
        </ul>
      ) : null}

      {run.warnings.length > 0 ? (
        <ul style={{ margin: 0, paddingLeft: 16, fontSize: 'var(--fs-micro)', color: 'var(--amber, #d6a84f)' }}>
          {run.warnings.map((warning) => (
            <li key={warning}>{warning}</li>
          ))}
        </ul>
      ) : null}

      {run.status === 'stopped' && run.handoff ? (
        <section aria-label="Frozen handoff">
          <strong>At Stop</strong>
          <p>Supervision ended. Ordinary ticket work continues.</p>
          <ul>
            {run.handoff.active.map((ticket) => (
              <li key={ticket.ticketId}>
                {ticket.ticketKey} ({ticket.state}): {ticket.attemptId === null ? 'no active attempt' : `${ticket.latestCompletedAttempt === undefined ? 'recorded' : 'active'} attempt ${ticket.attemptId}`}
                {ticket.branch ? ` · ${ticket.branch}` : ''}{ticket.head ? ` · ${ticket.head.slice(0, 12)}` : ''}
                {ticket.latestCompletedAttempt ? ` · latest ended attempt ${ticket.latestCompletedAttempt.id} (${ticket.latestCompletedAttempt.status})` : ''}
                {ticket.latestCompletedAttempt?.branch ? ` · ${ticket.latestCompletedAttempt.branch}` : ''}
                {ticket.latestCompletedAttempt?.head ? ` · ${ticket.latestCompletedAttempt.head.slice(0, 12)}` : ''}
              </li>
            ))}
          </ul>
        </section>
      ) : null}
      {run.load.tickets.length > 0 ? (
        <p className="dim" style={{ margin: 0, fontSize: 'var(--fs-micro)' }}>
          {run.status === 'stopped' ? 'Active now:' : 'Active:'}{' '}
          {run.load.tickets.map((ticket, index) => (
            <span key={ticket.ticketId}>
              {index > 0 ? ', ' : ''}
              <a
                href={`/p/${encodeURIComponent(run.projectSlug)}/tickets?ticket=${encodeURIComponent(ticket.ticketKey)}`}
                onClick={(event) => {
                  event.preventDefault();
                  onOpenTicket(ticket.ticketKey, undefined, run.projectSlug);
                }}
              >
                {ticket.ticketKey}
              </a>{' '}
              ({ticket.state})
            </span>
          ))}
        </p>
      ) : null}

      {run.status === 'enabled' && canManage ? (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button
            type="button"
            className="btn btn-warn btn-sm"
            disabled={stopping.kind === 'saving'}
            onClick={async () => {
              setStopping({ kind: 'saving' });
              try {
                await api.stopNightManager(run.id);
                setStopping({ kind: 'saved', at: Date.now() });
                await onChanged();
              } catch (error) {
                setStopping({ kind: 'failed', error: error instanceof Error ? error.message : String(error) });
              }
            }}
          >
            {stopping.kind === 'saving' ? 'Stopping…' : 'Stop'}
          </button>
          <span className="dim" style={{ fontSize: 'var(--fs-micro)' }}>
            {stopping.kind === 'failed'
              ? stopping.error
              : 'Stops supervision. Tickets already running continue through the normal factory.'}
          </span>
        </div>
      ) : null}
    </section>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="dim" style={{ fontSize: 'var(--fs-micro)' }}>
        {label}
      </dt>
      <dd style={{ margin: 0, fontVariantNumeric: 'tabular-nums' }}>{value}</dd>
    </div>
  );
}

function ExpandedView({
  project,
  run,
  onChanged,
  onOpenTicket,
}: {
  project: NightManagerProjectView;
  run: NightManagerRunView;
  onChanged: () => Promise<void> | void;
  onOpenTicket: (key: string, run?: number, project?: string | null) => void;
}) {
  const [events, setEvents] = useState<NightManagerEventView[]>([]);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState<SaveState>({ kind: 'idle' });

  useEffect(() => {
    let active = true;
    const read = () =>
      api
        .nightManagerEvents(run.id)
        .then((result) => {
          if (active) setEvents(result.events);
        })
        .catch(() => undefined);
    void read();
    const timer = window.setInterval(read, 10_000);
    return () => {
      active = false;
      window.clearInterval(timer);
    };
  }, [run.id]);

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      {run.progress.counted.length > 0 ? (
        <section aria-label="Counted tickets">
          <h3 style={{ margin: '0 0 4px', fontSize: 'var(--fs-small)' }}>Counted towards the total</h3>
          <ul style={{ margin: 0, paddingLeft: 16, fontSize: 'var(--fs-micro)' }}>
            {run.progress.counted.map((ticket) => (
              <li key={ticket.ticketId}>
                <a
                  href={`/p/${encodeURIComponent(run.projectSlug)}/tickets?ticket=${encodeURIComponent(ticket.ticketKey)}`}
                  onClick={(event) => {
                    event.preventDefault();
                    onOpenTicket(ticket.ticketKey, undefined, run.projectSlug);
                  }}
                >
                  {ticket.ticketKey}
                </a>{' '}
                {new Date(ticket.observedAt).toLocaleString()}
                {ticket.independent ? ' · completed independently' : ''}
                {ticket.reopenedAt ? ' · reopened since (still counted)' : ''}
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <section aria-label="Night manager activity">
        <h3 style={{ margin: '0 0 4px', fontSize: 'var(--fs-small)' }}>Activity</h3>
        {events.length === 0 ? (
          <p className="dim" style={{ margin: 0, fontSize: 'var(--fs-micro)' }}>Nothing yet.</p>
        ) : (
          <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 4, fontSize: 'var(--fs-micro)' }}>
            {events.map((event) => (
              <li key={event.id} className={event.kind === 'error' || (event.kind === 'warning' && event.payload.kind !== 'periodic-schedule') ? 'dim' : undefined}>
                <span className="dim">{new Date(event.createdAt).toLocaleTimeString()} </span>
                {describeEvent(event)}
                {event.payload.actionReport && typeof event.payload.actionReport === 'object' && 'timeline' in event.payload.actionReport && Array.isArray(event.payload.actionReport.timeline) ? (
                  <details><summary>Recorded action timeline</summary><ul>{event.payload.actionReport.timeline.map((row: { at: number; detail: string }, index: number) => <li key={index}>{new Date(row.at).toLocaleTimeString()}: {row.detail}</li>)}</ul></details>
                ) : null}
                {typeof event.payload.explanation === 'string' ? (
                  <details><summary>Model explanation</summary><p>{event.payload.explanation}</p></details>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </section>

      {run.status === 'enabled' && project.canManage ? (
        <section aria-label="Send a message">
          <form
            style={{ display: 'grid', gap: 4 }}
            onSubmit={async (event) => {
              event.preventDefault();
              if (!message.trim()) return;
              setSending({ kind: 'saving' });
              try {
                await api.messageNightManager(run.id, { body: message.trim(), requestId: crypto.randomUUID() });
                setMessage('');
                setSending({ kind: 'saved', at: Date.now() });
                await onChanged();
              } catch (error) {
                setSending({ kind: 'failed', error: error instanceof Error ? error.message : String(error) });
              }
            }}
          >
            <label htmlFor="night-manager-message" className="dim" style={{ fontSize: 'var(--fs-micro)' }}>
              Message the manager
            </label>
            <textarea
              id="night-manager-message"
              className="input"
              rows={2}
              value={message}
              onChange={(event) => setMessage(event.target.value)}
              placeholder="Prefer the CI failures tonight"
            />
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <button type="submit" className="btn btn-sm" disabled={sending.kind === 'saving' || !message.trim()}>
                {sending.kind === 'saving' ? 'Sending…' : 'Send'}
              </button>
              <span className="dim" style={{ fontSize: 'var(--fs-micro)' }}>
                {sending.kind === 'failed'
                  ? sending.error
                  : sending.kind === 'saved'
                    ? 'Sent — it will reach the manager on its next turn.'
                    : 'A message is direction. It cannot extend a deadline or raise a target; change those in the settings below.'}
              </span>
            </div>
          </form>
        </section>
      ) : null}

      {run.status === 'enabled' && project.canManage ? (
        <GoalBuilder project={project} run={run} onEnabled={onChanged} />
      ) : null}

      {run.status === 'stopped' ? (
        <p className="dim" style={{ margin: 0, fontSize: 'var(--fs-micro)' }}>
          This run is history and cannot be edited. Run again starts a fresh run from the saved project defaults.
        </p>
      ) : null}
    </div>
  );
}
